import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HOW_IT_WORKS_TOPICS,
  resolveHowItWorksTopic,
} from "./howItWorksTopics";

const Section = ({ title, children }) => (
  <section className="how-it-works-panel__section">
    <h3 className="how-it-works-panel__section-title">{title}</h3>
    {children}
  </section>
);

const BulletList = ({ items, t }) => (
  <ul className="how-it-works-panel__list">
    {items.map((key) => (
      <li key={key}>{t(key)}</li>
    ))}
  </ul>
);

const ImpactList = ({ impacts, t }) => (
  <ul className="how-it-works-panel__list how-it-works-panel__list--impacts">
    {impacts.map((impact) => (
      <li key={impact.id}>
        <span className="how-it-works-panel__impact-label">
          {t(impact.labelKey)}
        </span>
        {impact.irreversible && (
          <span className="how-it-works-panel__irreversible">
            {t("howItWorks.irreversible")}
          </span>
        )}{" "}
        <span className="how-it-works-panel__impact-text">
          {t(impact.textKey)}
        </span>
      </li>
    ))}
  </ul>
);

/**
 * Panneau latéral explicatif, identique sur toutes les pages : en quelques
 * mots, dans la pratique, ce que ça implique, à retenir.
 */
const HowItWorksPanel = ({ topic, context, isOpen, onClose }) => {
  const { t } = useTranslation();
  const content = resolveHowItWorksTopic(HOW_IT_WORKS_TOPICS[topic], context);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !content) return null;

  return createPortal(
    <div className="how-it-works-overlay" onClick={onClose}>
      <aside
        className="how-it-works-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t(content.titleKey)}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="how-it-works-panel__header">
          <div className="how-it-works-panel__heading">
            <span className="how-it-works-panel__icon" aria-hidden>
              <i className="fas fa-question-circle" />
            </span>
            <div>
              <p className="how-it-works-panel__eyebrow">
                {t("howItWorks.buttonLabel")}
              </p>
              <h2 className="how-it-works-panel__title">
                {t(content.titleKey)}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="how-it-works-panel__close"
            onClick={onClose}
            aria-label={t("Close")}
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </header>

        <div className="how-it-works-panel__body">
          {content.purpose.length > 0 ? (
            <Section title={t("howItWorks.sectionPurpose")}>
              <BulletList items={content.purpose} t={t} />
            </Section>
          ) : null}

          {content.howTo.length > 0 ? (
            <Section title={t("howItWorks.sectionHowTo")}>
              <BulletList items={content.howTo} t={t} />
            </Section>
          ) : null}

          {content.impacts.length > 0 ? (
            <Section title={t("howItWorks.sectionImpacts")}>
              <ImpactList impacts={content.impacts} t={t} />
            </Section>
          ) : null}

          {content.notes.length > 0 ? (
            <Section title={t("howItWorks.sectionNotes")}>
              <BulletList items={content.notes} t={t} />
            </Section>
          ) : null}

          {content.links.length > 0 && (
            <Section title={t("howItWorks.sectionLinks")}>
              <ul className="how-it-works-panel__links">
                {content.links.map((link) => (
                  <li key={link.id}>
                    {link.href ? (
                      <a
                        href={link.href}
                        download={link.download || true}
                        onClick={onClose}
                      >
                        <i className="fas fa-download" aria-hidden />
                        {t(link.labelKey)}
                      </a>
                    ) : (
                      <Link to={link.to} onClick={onClose}>
                        <i className="fas fa-arrow-right" aria-hidden />
                        {t(link.labelKey)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default HowItWorksPanel;
